import type { GetStaticPaths, GetStaticProps } from "next"
import { MarkdownContentRenderer } from "@/components/resources/components/MarkdownContentRenderer"
import { PageMeta } from "@/components/resources/components/Seo/PageMeta"
import {
  isResourceCategory,
  RESOURCE_CATEGORIES,
  type ResourceCategorySlug,
} from "@/components/resources/data/markdown/categories"
import {
  getAllTeasers,
  getPost,
  getPostSlugs,
  type MarkdownPost,
} from "@/components/resources/data/markdown/posts"
import { SiteLayout } from "@/components/resources/layouts/site"
import { ArticlePage } from "@/components/resources/templates/ArticlePage"

const Post = ({ post, mdxSource, meta }: MarkdownPost) => (
  <>
    <PageMeta meta={meta} />
    <ArticlePage {...post}>
      <MarkdownContentRenderer source={mdxSource} />
    </ArticlePage>
  </>
)

Post.getLayout = SiteLayout

export default Post

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: (Object.keys(RESOURCE_CATEGORIES) as ResourceCategorySlug[]).flatMap((category) =>
    getPostSlugs(category).map((slug) => ({ params: { category, slug } })),
  ),
  fallback: false,
})

export const getStaticProps: GetStaticProps<
  MarkdownPost,
  { category: string; slug: string }
> = async ({ params }) => {
  const { category, slug } = params ?? {}
  if (!category || !slug || !isResourceCategory(category)) {
    return { notFound: true }
  }

  const post = await getPost(category, slug)
  if (!post) {
    return { notFound: true }
  }

  // "Continue reading": the three newest posts of any category
  const related = (await getAllTeasers())
    .filter((teaser) => teaser.category !== category || teaser.slug !== slug)
    .slice(0, 3)

  return {
    props: { ...post, post: { ...post.post, related } },
    revalidate: 180,
  }
}
